import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Determine the initial theme from the class the blocking script in
// _document.tsx already applied to <html>, falling back to stored/system
// preference. This keeps React state in sync with the pre-paint theme and
// avoids overriding it (which would cause a flash).
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light'; // SSR default; the blocking script corrects this pre-paint.
  }

  // The blocking script in _document.tsx sets this class before hydration.
  if (document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  if (document.documentElement.classList.contains('light')) {
    return 'light';
  }

  // Fallback if the class somehow isn't present.
  const savedTheme = localStorage.getItem('theme') as Theme | null;
  if (savedTheme === 'dark' || savedTheme === 'light') {
    return savedTheme;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lazy initializer reads the already-applied theme instead of forcing 'light'.
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    // Re-sync on mount in case SSR rendered the default 'light'.
    setTheme(getInitialTheme());
  }, []);

  useEffect(() => {
    // This effect should also only run on the client
    if (typeof window !== 'undefined') {
      // Update document class when theme changes
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light'); // Ensure light is removed
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light'); // Add light class
      }

      // Pin native UI (scrollbars, form controls) to the app theme, not the OS.
      document.documentElement.style.colorScheme = theme;

      // Save preference to localStorage
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
